import { Component, ChangeDetectionStrategy } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { IrbAuthenticationService } from "@irb/irb-authentication/irb-authentication.service";
import { IAlertDialogData, IUserDetails } from "@irb/app.model";
import { IrbAppEvents } from "@irb/app.events";
import { AppService } from "@irb/app.service";
import { AnalyticsService } from "@irb-common/analytics/analytics.service";
import { PreferencesService } from "@irb-common/services/preferences.service";
import {Subscription} from "rxjs";

@Component({ standalone: false,
  selector: "dux-irb-authentication",
  templateUrl: "./irb-authentication.component.html",
  styleUrls: ["./irb-authentication.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrbAuthenticationComponent {
  message: string;
  authenticationEjbErrorPage: boolean = false;
  private getPrefSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private readonly router: Router,
    private authService: IrbAuthenticationService,
    private as: AppService,
    public ats: AnalyticsService,
    public pref: PreferencesService
  ) {
    this.ats.trackLoginEvent();

    if(!this.authService.hasToken()) {
      if (window.location.hostname === "localhost") {
        this.setAuthToken();
      } else {
        this.getNotLocalToken(() => { this.getAppConfigDetails(); });
        this.authService.startAuthTokenSubscription(() => {
          this.getNotLocalToken(() => { 
            this.authService.validateToken().subscribe((_data: any) => {});
          });
        });
      }
    }
  }

  getNotLocalToken(cbFunc: Function) {
    this.authService.getToken().subscribe((res: any) => {
      this.authService.tokenValidateAndSet("Bearer " + res.EAT);
      this.authService.setGatewayURL(res.jkfUrl);
      this.authService.setSacContextGatewayURL(res.sacContextGatewayUrl);
      cbFunc();
    }, () => {
      this.authService.handleTokenError();
    });
  }

  setAuthToken() {
    this.route.queryParams.subscribe((params) => {
      let token = params["Authorization"] || window.atob(window.sessionStorage.getItem("IRB_AUTH_TOKEN"));
      if(this.authService.tokenValidateAndSet(token)) {
        this.getAppConfigDetails();
      } else {
        this.message = "Authorization required.";
      }
    });
  }

  getAppConfigDetails() {
    this.authService.validateToken().subscribe((data: any) => {
      const authResp = data?.data;
      if (authResp != null && (authResp.errors?.length > 0 || authResp.error != null)) {
        this.showAccessDeniedDialog();
      } else {
        this.authService.getDetailForInitialLoad().subscribe((data: any) => {
          if (data.responseStatus === "EXCEPTION") {
            this.showAccessDeniedDialog();
          } else {
            const userDetails = data as IUserDetails;
            if (userDetails.featureFlags) {
              userDetails.isTlhWsaEnabled = userDetails.featureFlags.isTlhWsaEnabled;
            }
            this.as.setItem("IRB_APP_CONFIG", userDetails);

            if (userDetails.isAccessDenied) {
                this.showAccessDeniedDialog();
                return;
              }

              this.pref.getIrebalPreferences();
              let hasExchangeAgreementSigned: boolean = userDetails.hasExchangeAgreementSigned;
              let redirectPage: string = userDetails.redirectPage;

              if (hasExchangeAgreementSigned) {
                  if (userDetails.isModelProvider || userDetails.isMMCAdmin) {
                    this.getPrefSubscription = this.pref.data$.subscribe(() => {
                      let p = window.sessionStorage.getItem("IRB_PATH");
                      if (!p || !(p.includes("pmm-report"))) { p = "/models/model-market-center"; } //simple search for allowed menus as there are only 2 now
                      setTimeout(() => {
                        this.router.navigate([p]);}, 0);
                      this.getPrefSubscription.unsubscribe();
                    });

                    return;
                  }

                  const iRebalDLCoockie = this.authService.getIrebalDLCookie();
                  if (iRebalDLCoockie === 'IREBAL_MMC') {
                    if (!userDetails.isModelSecManager || (userDetails.isModelSecManager && userDetails.isHideModelMarketCenter)) {
                      this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                        dType: 'WARN',
                        dTitle: 'You’re not set up for Model Market Center',
                        msg: "Model Market Center requires additional permissions.",
                        altMsg: "To get access, contact your Firm Security Administrator (FSA) or the iRebal support team at 855-640-2472.",
                        altMsgHTML: "<div style='text-align: center;'><a href='https://advisorservices.schwab.com/model-market-center' target='_blank'>Learn about Model Market Center</a></div>",
                        onProceed: () => {
                          this.router.navigate(["/home"]);
                        },
                        dProceedBtnLabel: 'Go to iRebal'
                      } as IAlertDialogData);
                    } else {
                      this.router.navigate(["/models/model-market-center"]);
                    }
                    this.authService.deleteIrebalDLCookie();
                    return;
                  }

                  if (redirectPage) {
                    switch (redirectPage) {
                      case "HOME":
                        this.router.navigate(["/home"]);
                        break;
                      case "GUIDED_SETUP":
                        this.router.navigate(["/initial-setup"]);
                        break;
                      case "ADMIN_SETTINGS":
                        this.router.navigate(["/initial-setup"]);
                        break;
                      case "NON_ADMIN_SETTINGS":
                        this.router.navigate(["/initial-setup"]);
                        break;
                    }
                  } else {
                    this.getPrefSubscription = this.pref.data$.subscribe(() => {
                      let p = window.sessionStorage.getItem("IRB_PATH") || "/home";
                      setTimeout(() => {
                        this.router.navigate([p]);}, 0);
                      this.getPrefSubscription.unsubscribe();
                    });
                  }
              } else {
                if (redirectPage === "AUTHENTICATION_EJB_ERROR") {
                  this.authenticationEjbErrorPage = false;
                } else {
                  this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                    msg: "Please sign the agreement",
                    onCancel: () => this.logOut(),
                    onProceed: () => this.logOut(),
                  } as IAlertDialogData);
                }
              }
            }
          }, () => {
            this.message = "iRebal App config details failed";
          });
      }});
  }

  showAccessDeniedDialog() {
    this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
      dType: 'ERROR',
      dTitle: 'Access Denied',
      msg: "There may be a problem with your user set-up.",
      altMsg: "Please contact your FSA or iRebal support team to correct your user configuration at 855-640-2472.",
      onProceed: () => {
        this.as.logOut();
      }
    } as IAlertDialogData);
  }

  logOut() {
    this.as.logOut();
  }
}
